import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../Reveal";
import { SERVICE_GROUPS, IMAGES, COUNTRIES_COUNT } from "../../lib/site";

const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
const MotionLink = motion(Link);

const FEATURES = {
  "Visa Services": { image: IMAGES.traveler, tag: `${COUNTRIES_COUNT} countries served`, desc: "Business & tourist visas hand-carried to DC embassies." },
  "Document Services": { image: IMAGES.booklet, tag: "State Dept. authentication", desc: "Apostilles & certifications recognized worldwide." },
};

const Card = ({ item, i, className = "" }) => {
  const Icon = item.icon;
  return (
    <MotionLink
      to={item.to}
      onClick={scrollTop}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
      className={`group block bg-white rounded-2xl border border-slate-200 p-7 hover:border-[#0b192c] hover:shadow-[0_12px_40px_rgba(11,25,44,0.08)] hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-300 ${className}`}
      data-testid={item.testid}
    >
      <div className="flex items-start justify-between">
        <div className="w-11 h-11 rounded-xl bg-[#d90429]/10 group-hover:bg-[#d90429] flex items-center justify-center transition-colors duration-300">
          <Icon className="w-5 h-5 text-[#d90429] group-hover:text-white transition-colors duration-300" />
        </div>
        <ArrowUpRight className="w-5 h-5 text-slate-300 group-hover:text-[#d90429] transition-colors" />
      </div>
      <h4 className="mt-5 font-semibold text-lg text-[#0b192c]">{item.title}</h4>
      <p className="mt-2 text-[15px] text-slate-600 leading-relaxed">{item.desc}</p>
    </MotionLink>
  );
};

const FeaturePanel = ({ feature, to, label }) => (
  <Link
    to={to}
    onClick={scrollTop}
    className="group relative rounded-2xl overflow-hidden sm:col-span-2 lg:col-span-4 lg:row-span-2 min-h-[220px] lg:min-h-full border border-black/5"
    data-testid={`services-feature-${label}`}
  >
    <img src={feature.image} alt={label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0b192c] via-[#0b192c]/50 to-[#0b192c]/10" />
    <div className="relative h-full flex flex-col justify-end p-7 text-white">
      <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#ef233c]">{feature.tag}</span>
      <h4 className="mt-2 font-display font-bold text-2xl leading-tight">{label}</h4>
      <p className="mt-2 text-white/75 text-[15px] leading-relaxed">{feature.desc}</p>
      <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-[gap]">
        View all <ArrowUpRight className="w-4 h-4" />
      </span>
    </div>
  </Link>
);

export const Services = () => (
  <section id="services" className="bg-white py-24 md:py-32" data-testid="services">
    <div className="max-w-7xl mx-auto px-6 md:px-8">
      <Reveal>
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#d90429]">What we do</span>
        <div className="mt-3 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#0b192c] tracking-tight max-w-2xl">
            Passport, visa & document services — done right, done fast.
          </h2>
          <p className="text-slate-600 max-w-sm">Every service is handled by specialists who hand-carry your filings to the agencies and embassies in downtown DC.</p>
        </div>
      </Reveal>

      <div className="mt-14 space-y-16">
        {SERVICE_GROUPS.map((group, gi) => {
          const feature = FEATURES[group.label];
          const items = group.items.map((it, i) => ({ ...it, testid: `service-card-${gi}-${i}` }));
          return (
            <div key={group.label}>
              <Reveal delay={0.05}>
                <div className="flex items-center justify-between border-b border-slate-200 pb-4 mb-8">
                  <h3 className="font-display font-bold text-xl md:text-2xl text-[#0b192c]">{group.label}</h3>
                  <Link to={group.to} onClick={scrollTop} className="text-sm font-semibold text-[#d90429] hover:gap-2 inline-flex items-center gap-1 transition-[gap]" data-testid={`services-viewall-${gi}`}>
                    View all <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </Reveal>

              {feature ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-5 auto-rows-fr">
                  <FeaturePanel feature={feature} to={group.to} label={group.label} />
                  {items.map((item, i) => (
                    <Card key={item.title} item={item} i={i} className="lg:col-span-4" />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {items.map((item, i) => (
                    <Card key={item.title} item={item} i={i} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  </section>
);
